import { ConfirmationDialog, RadioChangeEvent, useOpen } from "@dgs/core";
import { useFormikContext } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FilterMode, OnDemandFilterFormValues } from "../filterTypes";
import { emptyQuickFilterItems } from "../utils";

const SwitchContainer = styled.div<{ $color: "background" | "content" }>`
	background-color: ${({ $color, theme }) => theme.colors.layout[$color].background};
	padding: ${({ theme }) => theme.spacing(1)};
`;

interface Props {
	name: string;
	color: "content" | "background";
}

export const FilterModeSwitcher: FC<Props> = ({ name, color }) => {
	const { t } = useTranslation();
	const { values, setValues } = useFormikContext<OnDemandFilterFormValues>();
	const { open, handleOpen, handleClose } = useOpen();

	return (
		<>
			<SwitchContainer $color={color}>
				<SwitchInput
					name={name}
					value={FilterMode.QUICK}
					label={t("Quick")}
					checked={values.mode === FilterMode.QUICK}
					onSelect={
						!values.filterItems.length
							? () => setValues({ mode: FilterMode.QUICK, filterItems: emptyQuickFilterItems })
							: handleOpen
					}
				/>
				<SwitchInput
					name={name}
					value={FilterMode.ADVANCED}
					label={t("Advanced")}
					checked={values.mode === FilterMode.ADVANCED}
					onSelect={() =>
						setValues({
							mode: FilterMode.ADVANCED,
							filterItems: values.filterItems.filter(({ value }) => value !== null && value.length > 0),
						})
					}
				/>
			</SwitchContainer>
			<ConfirmationDialog
				heading={t("Are you sure you want to switch to quick mode?")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
				open={open}
				onClose={handleClose}
				action={() => {
					setValues({ mode: FilterMode.QUICK, filterItems: emptyQuickFilterItems });
					handleClose();
				}}
			>
				{t("Switching to quick mode will reset all of the filter elements.")}
			</ConfirmationDialog>
		</>
	);
};

interface SwitchProps {
	name: string;
	label: string;
	value: FilterMode;
	checked: boolean;
	onSelect: (value: FilterMode) => void;
}

const RadioInput = styled.input`
	display: none;

	&:checked + label {
		background-color: ${({ theme }) => theme.colors.palette.primary.main.base};
		color: ${({ theme }) => theme.colors.palette.primary.main.contrast};
	}
`;

const Label = styled.label`
	display: inline-flex;
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
	padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
	transition: all 0.5s ease-in-out;
	justify-content: center;

	&:hover {
		cursor: pointer;
	}
`;

const SwitchInput: FC<SwitchProps> = ({ name, label, value, checked, onSelect }) => {
	return (
		<>
			<RadioInput
				type="radio"
				id={`${name}${label}`}
				name={name}
				value={value}
				checked={checked}
				onChange={(e: RadioChangeEvent) => onSelect(e.target.value as FilterMode)}
			/>
			<Label htmlFor={`${name}${label}`}>{label}</Label>
		</>
	);
};
